import { Badge } from "@/components/ui/badge";
import { Target, Trophy, Users, Megaphone, TrendingUp, AlertCircle } from "lucide-react";

interface AuditResultsSummaryProps {
  reportData: any;
  expanded?: boolean;
}

export function AuditResultsSummary({ reportData, expanded = false }: AuditResultsSummaryProps) {
  const performanceScore = reportData?.benchmarks?.performanceScore || 0;
  const ranking = reportData?.benchmarks?.ranking || 0;
  const totalCompetitors = reportData?.competitors?.length || 0;
  const employeeActivity = reportData?.employeeData?.avgActivityScore || 0;
  const hasActiveAds = reportData?.company?.adsData?.hasActiveAds || false;
  const followerCount = reportData?.company?.metrics?.followerCount || 0;

  const getScoreColor = (score: number) => {
    if (score >= 70) return "text-green-600";
    if (score >= 40) return "text-yellow-600";
    return "text-red-600";
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Get confidence levels
  const confidenceLevels = reportData.meta?.confidenceLevels;
  const hasLowConfidence = confidenceLevels && (
    confidenceLevels.linkedInCompanyData === 'low' ||
    confidenceLevels.employeeData === 'low' ||
    confidenceLevels.competitorData === 'low' ||
    confidenceLevels.overall === 'low'
  );
  
  // Check for zero metrics (indicates data collection failure)
  const hasZeroMetrics = 
    followerCount === 0 && 
    (reportData.company?.metrics?.employeeCount ?? 0) === 0 &&
    (reportData.company?.metrics?.recentPosts?.length ?? 0) === 0;

  if (expanded) {
    return (
      <div className="space-y-3">
        {/* Critical warning for zero metrics */}
        {hasZeroMetrics && hasLowConfidence && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-md">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800 dark:text-red-200">Brak danych LinkedIn</p>
              <p className="text-xs text-red-700 dark:text-red-300">
                Nie udało się pobrać metryk. Możliwe przyczyny: kodowanie URL, prywatność profilu, limity API.
              </p>
            </div>
          </div>
        )}
        
        {/* Standard low confidence warning */}
        {hasLowConfidence && !hasZeroMetrics && (
          <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
            <div className="flex-1">
              <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200">Część danych szacunkowa</p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                {confidenceLevels.linkedInCompanyData === 'low' && 'LinkedIn, '}
                {confidenceLevels.employeeData === 'low' && 'Pracownicy, '}
                {confidenceLevels.competitorData === 'low' && 'Konkurencja'}
              </p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Performance</p>
              <p className={`text-lg font-bold ${getScoreColor(performanceScore)}`}>
                {performanceScore}/100
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-600" />
            <div>
              <p className="text-xs text-muted-foreground">Ranking</p>
              <p className="text-lg font-bold">
                #{ranking} / {totalCompetitors}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            <div>
              <p className="text-xs text-muted-foreground">Aktywność</p>
              <p className={`text-lg font-bold ${getScoreColor(employeeActivity)}`}>
                {employeeActivity}/100
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-purple-600" />
            <div>
              <p className="text-xs text-muted-foreground">Reklamy</p>
              <Badge variant={hasActiveAds ? "default" : "outline"}>
                {hasActiveAds ? "✓ Aktywne" : "Brak"}
              </Badge>
            </div>
          </div>

          {followerCount > 0 && (
            <div className="flex items-center gap-2 col-span-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <div>
                <p className="text-xs text-muted-foreground">Obserwujący</p>
                <p className="text-lg font-bold">{formatNumber(followerCount)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Compact view for table
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-1 text-xs">
      <div className="flex items-center gap-2">
        <Target className="h-3 w-3" />
        <span className={`font-semibold ${getScoreColor(performanceScore)}`}>
          {performanceScore}/100
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Trophy className="h-3 w-3" />
        <span>#{ranking} / {totalCompetitors}</span>
      </div>
      <div className="flex items-center gap-2">
        <Users className="h-3 w-3" />
        <span className={getScoreColor(employeeActivity)}>
          {employeeActivity}/100
        </span>
      </div>
      {hasActiveAds && (
        <Badge variant="default" className="text-xs w-fit">
          <Megaphone className="h-3 w-3 mr-1" />
          Ads ✓
        </Badge>
      )}
      </div>
      
      {hasZeroMetrics && hasLowConfidence && (
        <Badge variant="outline" className="text-xs border-red-600 text-red-700 dark:text-red-500 w-fit">
          <AlertCircle className="h-3 w-3 mr-1" />
          Brak danych
        </Badge>
      )}
      
      {hasLowConfidence && !hasZeroMetrics && (
        <Badge variant="outline" className="text-xs border-yellow-600 text-yellow-700 dark:text-yellow-500 w-fit">
          <AlertCircle className="h-3 w-3 mr-1" />
          Niepewne dane
        </Badge>
      )}
    </div>
  );
}
